import { useEffect } from "react";
import { useQuery } from "@tanstack/react-query";
import { ArrowRight, ShieldCheck } from "lucide-react";
import { Link, useParams } from "react-router-dom";

import { rememberGrowthReferral, validateGrowthReferral } from "@/entities/growth-network";
import { useAuthStore } from "@/entities/auth/store";
import { Button } from "@/shared/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/shared/ui/card";
import { OrkexraLogo } from "@/shared/ui/OrkexraLogo";

export default function GrowthReferralJoinPage(): JSX.Element {
  const { referralCode = "" } = useParams();
  const token = useAuthStore((state) => state.accessToken);
  const query = useQuery({
    queryKey: ["growth-referral", referralCode],
    queryFn: () => validateGrowthReferral(referralCode),
    enabled: Boolean(referralCode), retry: false,
  });

  useEffect(() => {
    if (query.data?.valid) rememberGrowthReferral(query.data.referral_code);
  }, [query.data]);

  const destination = token ? "/workspaces/new" : `/login?mode=register&ref=${encodeURIComponent(query.data?.referral_code ?? referralCode)}`;
  return (
    <main className="flex min-h-screen items-center justify-center bg-background px-5 py-12 text-[var(--md-ivory-0)]" data-testid="growth-referral-join">
      <Card className="w-full max-w-xl border-[color:var(--md-amber)]/30 bg-[var(--md-ink-1)]/90">
        <CardHeader className="space-y-5 text-center"><OrkexraLogo variant="wordmark" size={30} /><CardTitle className="font-display text-3xl">Your business deserves a system that remembers.</CardTitle></CardHeader>
        <CardContent className="space-y-5 text-center">
          {query.isLoading && <p className="text-[var(--md-fog)]">Checking your invitation…</p>}
          {query.isError && <div className="rounded-lg border border-red-500/30 bg-red-500/5 p-4 text-sm text-red-200">This invitation is unavailable. Ask the person who invited you for a current link.</div>}
          {query.data && <>
            <p className="text-[var(--md-fog)]">You were invited to Orkexra—the governed operating system that connects business intelligence, campaigns, content, review, and growth.</p>
            <div className="flex items-center justify-center gap-2 text-sm text-emerald-300"><ShieldCheck className="h-4 w-4" />Invitation verified</div>
            <Button asChild size="lg" className="w-full"><Link to={destination}>{token ? "Create your workspace" : "Create your Orkexra account"}<ArrowRight className="ml-2 h-4 w-4" /></Link></Button>
            <p className="text-xs text-[var(--md-fog)]">No recruiting pitch. No downline. Just a better operating system for your business.</p>
          </>}
        </CardContent>
      </Card>
    </main>
  );
}
